import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, Inbox, Target, CalendarPlus, User, Image as ImageIcon, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { StatCard } from "@/components/DashboardShell";
import { PlanDialog, ScheduleDialog } from "@/components/AccountabilityDialogs";
import { ACTIVITY_STATUS_COLORS, formatDate } from "@/data/accountability";
import api, { formatApiError } from "@/lib/api";

export function PartnerAccountability() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [reason, setReason] = useState("");
  const [viewPhoto, setViewPhoto] = useState(null);
  const [planFor, setPlanFor] = useState(null);     // {matchId, plan}
  const [schedFor, setSchedFor] = useState(null);   // {matchId, activity}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/partner/accountability");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async (id) => {
    setActing(id);
    try {
      await api.post(`/checkins/${id}/verify`);
      toast.success("Check-in verified.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const doReject = async () => {
    if (reason.trim().length < 3) { toast.error("Please give a short reason."); return; }
    setActing(rejectId);
    try {
      await api.post(`/checkins/${rejectId}/reject`, { reason });
      toast.success("Check-in rejected.");
      setRejectId(null); setReason("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  const confirmPlan = async (planId) => {
    setActing(planId);
    try {
      await api.post(`/plans/${planId}/confirm`);
      toast.success("Plan confirmed — now active.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  if (loading) return <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 text-[#E53E3E] animate-spin" /></div>;
  if (!data) return null;

  const { members = [], pending_checkins = [], performance } = data;

  return (
    <div className="mt-8" data-testid="partner-accountability">
      <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4">Accountability</h2>

      {/* Performance */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="partner-performance">
        <StatCard label="Active Members" value={performance.active_members} />
        <StatCard label="Reviews Done" value={performance.checkins_reviewed} />
        <StatCard label="Verified" value={performance.verified_activities} />
        <StatCard label="Rejected" value={performance.rejected_checkins} />
        <StatCard label="Pending" value={performance.pending_reviews} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pending check-ins */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid="partner-pending-checkins">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><Inbox className="w-5 h-5 text-[#E53E3E]" /> Awaiting Verification</h3>
          {pending_checkins.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500">No check-ins awaiting review.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {pending_checkins.map((c) => (
                <div key={c.id} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4" data-testid={`partner-checkin-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{c.member?.display_name}</p>
                    <span className="text-xs text-zinc-500">{formatDate(c.date)}</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-1 capitalize">{c.activity?.replace("-", " ")}</p>
                  {c.note && <p className="text-sm text-zinc-500 mt-1">“{c.note}”</p>}
                  {c.photo && <button onClick={() => setViewPhoto(c.photo)} className="mt-2 inline-flex items-center gap-1 text-xs text-[#E53E3E] hover:underline"><ImageIcon className="w-3.5 h-3.5" /> View evidence</button>}
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" onClick={() => verify(c.id)} disabled={acting === c.id} className="flex-1 bg-green-600 hover:bg-green-700 rounded-full" data-testid={`partner-verify-${c.id}`}>
                      {acting === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Verify</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setRejectId(c.id); setReason(""); }} className="flex-1 border-zinc-700 bg-transparent hover:bg-[#E53E3E] hover:text-white rounded-full" data-testid={`partner-reject-${c.id}`}><X className="w-4 h-4 mr-1" /> Reject</Button>
                  </div>
                  {rejectId === c.id && (
                    <div className="mt-2 flex gap-2">
                      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection" className="h-9 bg-zinc-900 border-zinc-800" data-testid={`partner-reject-reason-${c.id}`} />
                      <Button size="sm" onClick={doReject} className="bg-[#E53E3E] hover:bg-[#C53030] rounded-full" data-testid={`partner-reject-confirm-${c.id}`}>Send</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Members + plans + activities */}
        <div className="lg:col-span-2 space-y-4" data-testid="partner-members-accountability">
          {members.length === 0 ? (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6"><p className="text-sm text-zinc-500">No active members yet. Accept a match request to begin.</p></div>
          ) : members.map((mb) => {
            const upcoming = (mb.activities || []).filter((a) => a.status === "Scheduled");
            const plan = mb.plan;
            const canConfirm = plan && plan.status === "proposed" && plan.proposed_by !== "partner";
            return (
              <div key={mb.match_id} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6" data-testid={`partner-member-acc-${mb.match_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-zinc-700 bg-zinc-800 grid place-items-center shrink-0">
                    {mb.member?.photo ? <img src={mb.member.photo} alt={mb.member.display_name} className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-zinc-500" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-lg font-bold uppercase">{mb.member?.display_name}</p>
                    <p className="text-xs text-zinc-500">This week: {mb.progress.this_week_completed}/{mb.progress.weekly_target || 0} · {mb.progress.streak}w streak · {mb.progress.total_verified} verified</p>
                  </div>
                </div>

                {/* Plan */}
                <div className="mt-4 rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Target className="w-4 h-4 text-[#E53E3E]" /> Plan</h4>
                    {plan && <Badge className={plan.status === "active" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400"}>{plan.status}</Badge>}
                  </div>
                  {plan ? (
                    <div className="mt-2">
                      <p className="font-medium">{plan.goal}</p>
                      <p className="text-xs text-zinc-500 capitalize">{plan.activity?.replace("-", " ")} · {plan.target_per_week}×/week{plan.days?.length ? ` · ${plan.days.join(", ")}` : ""}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {canConfirm && <Button size="sm" onClick={() => confirmPlan(plan.id)} disabled={acting === plan.id} className="bg-green-600 hover:bg-green-700 rounded-full" data-testid={`partner-confirm-plan-${mb.match_id}`}><Check className="w-4 h-4 mr-1" /> Confirm</Button>}
                        <Button size="sm" variant="outline" onClick={() => setPlanFor({ matchId: mb.match_id, plan })} className="border-zinc-700 bg-transparent hover:bg-zinc-800 rounded-full" data-testid={`partner-edit-plan-${mb.match_id}`}><Pencil className="w-4 h-4 mr-1" /> {plan.status === "active" ? "Propose Changes" : "Edit"}</Button>
                      </div>
                      {plan.status === "proposed" && plan.proposed_by === "partner" && <p className="mt-2 text-xs text-amber-400">Waiting for member to confirm.</p>}
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => setPlanFor({ matchId: mb.match_id, plan: null })} className="mt-2 bg-[#E53E3E] hover:bg-[#C53030] rounded-full" data-testid={`partner-create-plan-${mb.match_id}`}>Propose a Plan</Button>
                  )}
                </div>

                {/* Upcoming */}
                <div className="mt-3 flex items-center justify-between">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-zinc-500">Upcoming</h4>
                  <Button size="sm" variant="outline" onClick={() => setSchedFor({ matchId: mb.match_id, activity: plan?.activity })} className="border-zinc-700 bg-transparent hover:bg-zinc-800 rounded-full" data-testid={`partner-schedule-${mb.match_id}`}><CalendarPlus className="w-4 h-4 mr-1" /> Schedule</Button>
                </div>
                {upcoming.length === 0 ? (
                  <p className="mt-2 text-sm text-zinc-500">No upcoming activities.</p>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    {upcoming.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-lg border border-zinc-800 px-3 py-2">
                        <span className="text-sm capitalize">{a.activity?.replace("-", " ")} · <span className="text-zinc-500">{formatDate(a.date)}{a.time ? ` ${a.time}` : ""}</span></span>
                        <Badge className={ACTIVITY_STATUS_COLORS[a.status]}>{a.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {viewPhoto && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-6" onClick={() => setViewPhoto(null)} data-testid="evidence-viewer">
          <img src={viewPhoto} alt="Evidence" className="max-w-full max-h-[85vh] rounded-xl border border-zinc-700" />
        </div>
      )}
      {planFor && <PlanDialog open={!!planFor} onOpenChange={(o) => !o && setPlanFor(null)} matchId={planFor.matchId} plan={planFor.plan} onSaved={load} />}
      {schedFor && <ScheduleDialog open={!!schedFor} onOpenChange={(o) => !o && setSchedFor(null)} matchId={schedFor.matchId} defaultActivity={schedFor.activity} onSaved={load} />}
    </div>
  );
}
